import React, { useState } from 'react';
import { Tab } from '@headlessui/react';
import { Users, Database, Building2, Settings } from 'lucide-react';
import { UserManagement } from './UserManagement';
import { MinerManagement } from './MinerManagement';
import { FacilityManagement } from './FacilityManagement';
import { AdminSettings } from './AdminSettings';
import { SetupDatabase } from '../../components/admin/SetupDatabase';
import { useAuthContext } from '../../components/auth/AuthProvider';
import { Navigate } from 'react-router-dom';
import { Loader } from '../../components/common/Loader';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export function AdminDashboard() {
  const { user, profile, loading } = useAuthContext();
  const [selectedTab, setSelectedTab] = useState(0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (!user || profile?.role !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  const tabs = [
    { name: 'Users', icon: Users, component: UserManagement },
    { name: 'Miners', icon: Database, component: MinerManagement },
    { name: 'Facilities', icon: Building2, component: FacilityManagement },
    { name: 'Settings', icon: Settings, component: AdminSettings }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage users, miners, and facilities
          </p>
        </div>

        <div className="mb-8">
          <SetupDatabase />
        </div>

        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-4 border-b border-gray-200">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) => classNames(
                  selected
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2'
                )}
              >
                <tab.icon className="w-5 h-5" />
                {tab.name}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-6">
            {tabs.map((tab, index) => (
              <Tab.Panel key={index}>
                <tab.component />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
}